import { useCallback, useState } from "react";
import { useClassrooms } from "../data/ClassroomContext";
import useLearnerActivity from "../data/useLearnerActivity";
import useRelease from "../data/useRelease";
import { DENIED, OFFLINE } from "../data/problems";
import { CheckIcon, DeviceIcon } from "./Icons";
import ActivityPreviewDialog from "./ActivityPreviewDialog";
import ClassObserveDialog from "./ClassObserveDialog";
import ui from "./ui.module.css";

const PROBLEM_COPY = {
  [OFFLINE]: "You're offline. Releasing needs a connection.",
  [DENIED]: "You can't release this activity for this class.",
};

const Checking = () => (
  <p className={ui.meta} role="status">
    Checking learner tablets…
  </p>
);

const CheckFailed = ({ onRetry }) => (
  <section className={`${ui.card} ${ui.section} ${ui.stack}`}>
    <h2 className={ui.metaItem}>
      <DeviceIcon />
      Learner tablets
    </h2>
    <p className={`${ui.notice} ${ui.warning}`} role="alert">
      We couldn't check the learner tablets.
    </p>
    <button type="button" className={`${ui.button} ${ui.primary}`} onClick={onRetry}>
      Check again
    </button>
  </section>
);

// One clear classroom action at the learner-activity point. Teaching is
// already underway, so this never says "Start lesson" or "Launch quiz".
// Shows nothing when the lesson has no activity to release.
const ReleaseCard = ({ lessonId, blockId, record }) => {
  const activity = useLearnerActivity(lessonId, blockId);

  if (activity.status === "checking") return <Checking />;
  if (activity.status === "problem") return <CheckFailed onRetry={activity.retry} />;
  if (!activity.activities.length) return null;

  return (
    <div className={ui.stack}>
      {activity.activities.map((item) => (
        <ReleaseControls
          key={item.id}
          lessonId={lessonId}
          record={record}
          activity={item}
          showTitle={activity.activities.length > 1}
        />
      ))}
    </div>
  );
};

const ReleaseControls = ({ lessonId, record, activity, showTitle }) => {
  const activityId = activity.id;
  const state = useRelease({ lessonId, record, activityId });
  const classroomId = useClassrooms().active.id;
  const [previewing, setPreviewing] = useState(false);
  const [observing, setObserving] = useState(false);
  const closePreview = useCallback(() => setPreviewing(false), []);
  const closeObserve = useCallback(() => setObserving(false), []);

  if (state.status === "checking") return <Checking />;

  if (state.status === "released") {
    return (
      <>
        <section className={`${ui.card} ${ui.section} ${ui.stack}`} role="status">
          <span className={ui.pill}><CheckIcon size={16} />Activity released</span>
          <p>Learners can do the activity on their tablets. Carry on when you're ready.</p>
          <div className={ui.sheetActions}>
            <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={() => setPreviewing(true)}>Preview</button>
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={() => setObserving(true)}>Class progress</button>
          </div>
        </section>
        {previewing && <ActivityPreviewDialog classroomId={classroomId} activityId={activityId} onClose={closePreview} />}
        {observing && <ClassObserveDialog classroomId={classroomId} deliverySyncKey={record.syncKey} activityId={activityId} onClose={closeObserve} />}
      </>
    );
  }

  const failedRead = state.status === "problem" && state.reading;
  const message =
    state.status === "problem"
      ? failedRead
        ? "We couldn't check the learner tablets."
        : PROBLEM_COPY[state.problem] || "That didn't work. Please try again."
      : null;

  return (
    <section className={`${ui.card} ${ui.section} ${ui.stack}`}>
      <h2 className={ui.metaItem}>
        <DeviceIcon />
        {showTitle && activity.title ? activity.title : "Learner tablets"}
      </h2>
      <p>When learners are ready, send this activity to their tablets.</p>
      {message && (
        <p className={`${ui.notice} ${ui.warning}`} role="alert">
          {message}
        </p>
      )}
      <div className={ui.sheetActions}>
        {!failedRead && <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={() => setPreviewing(true)}>Preview</button>}
        <button type="button" className={`${ui.button} ${ui.primary}`} onClick={failedRead ? state.recheck : state.release} disabled={state.status === "releasing"}>
          {state.status === "releasing" ? "Sending…" : failedRead ? "Check again" : "Send to learners"}
        </button>
      </div>
      {previewing && <ActivityPreviewDialog classroomId={classroomId} activityId={activityId} onClose={closePreview} />}
    </section>
  );
};

export default ReleaseCard;
